import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Menu, Tooltip } from '@arco-design/web-react';
import { IconLaunch, IconMenuFold, IconMenuUnfold } from '@arco-design/web-react/icon';

import { RenderIcon } from '@/components';
import { useLeftMenu } from '@/hooks/useMenu';
import { NAV_MAP } from '@/hooks/useMenu/constants';
import {
  SUB_CENTER,
  SUB_CENTER_ICON,
  SUB_CENTER_ROOT_TITLE,
  buildExternalCenterUrlFromMenu,
  getEffectiveSubCenter,
} from '@/hooks/useMenu/subCenter';

import { filterCurrNav } from './utils';

const MenuItem = Menu.Item;
const MenuItemGroup = Menu.ItemGroup;
const CENTER_ORDER = [
  SUB_CENTER.COMPUTE,
  SUB_CENTER.AI,
  SUB_CENTER.APP,
  SUB_CENTER.RESOURCE,
  SUB_CENTER.CHARGE,
  SUB_CENTER.OPS,
  SUB_CENTER.BIZ,
];
const DATA_CENTER_ORDER = [SUB_CENTER.COMPUTE, SUB_CENTER.AI, SUB_CENTER.APP, SUB_CENTER.OPS, SUB_CENTER.BIZ, SUB_CENTER.RESOURCE];

/** 判断菜单节点是否包含当前页面，详情页按菜单路径前缀归入对应中心。 */
function containsPath(item: any, pathname: string): boolean {
  if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) return true;
  return item.children?.some((child) => containsPath(child, pathname)) || false;
}

/** 获取菜单节点的首个可访问页面，作为系统管理中心的切换入口。 */
function getFirstPath(item: any): string {
  if (item.path) return item.path;
  for (const child of item.children || []) {
    const path = getFirstPath(child);
    if (path) return path;
  }
  return '';
}

function renderGroupTitle(item: any) {
  const title = NAV_MAP[item.title]?.title || item.title;
  return (
    <Tooltip content={title} position="right">
      <span className="left-menu-label" style={{ fontSize: 12 }}>
        {title}
      </span>
    </Tooltip>
  );
}

function renderLeafMenuItem(child: any, marginLeft = -20) {
  const title = NAV_MAP[child.title]?.title || child.title;
  return (
    <MenuItem key={child.path} style={{ marginLeft }}>
      <RenderIcon size="16" Icon={child.icon} style={{ verticalAlign: 'middle' }} />
      <Link to={child.path}>
        <Tooltip content={title} position="right">
          <span className="left-menu-label">{title}</span>
        </Tooltip>
      </Link>
    </MenuItem>
  );
}

function Nav() {
  const { t } = useTranslation();
  const leftMenu = useLeftMenu();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [selKey, setSelKey] = useState<string>('0_1');
  const [menuCollapsed, setMenuCollapsed] = useState(false);

  const isSystem = pathname.startsWith('/system/');
  const systemCenters = isSystem ? (leftMenu?.children || []).flatMap((item) => (item.promoteChildren ? item.children || [] : [item])) : [];
  const systemCenter = isSystem ? systemCenters.find((item) => containsPath(item, pathname)) || systemCenters[0] : null;
  const showFunctionMenu = !isSystem || !!systemCenter?.children?.length;

  const { navs, bottomNavs } = useMemo(() => {
    if (!leftMenu || !leftMenu?.children)
      return {
        navs: [],
        bottomNavs: [],
      };
    const navs = [];
    const bottomNavs = [];
    const menuChildren = systemCenter ? systemCenter.children || [systemCenter] : leftMenu.children;
    menuChildren.forEach((item) => {
      if (item.fixBottom) {
        bottomNavs.push(item);
      } else {
        navs.push(item);
      }
    });

    return {
      navs,
      bottomNavs,
    };
  }, [leftMenu, systemCenter]);

  const subCenter = getEffectiveSubCenter(pathname);
  const currentCenter = systemCenter?.title || subCenter;
  const hasCurrentCenterAccess = isSystem || (!!subCenter && leftMenu?.title === SUB_CENTER_ROOT_TITLE[subCenter]);
  const centerNavs = useMemo(() => {
    if (isSystem) {
      return systemCenters.map((item) => ({ ...item, centerKey: item.title, targetPath: getFirstPath(item) }));
    }
    if (!currentCenter) return [];
    const centerOrder = pathname.startsWith('/data-center/') ? DATA_CENTER_ORDER : CENTER_ORDER;
    return [
      ...(hasCurrentCenterAccess
        ? [
            {
              title: SUB_CENTER_ROOT_TITLE[currentCenter],
              icon: SUB_CENTER_ICON[currentCenter],
              centerKey: currentCenter,
            },
          ]
        : []),
      ...bottomNavs.filter((item) => item.externalLink),
    ].sort((a, b) => centerOrder.indexOf(a.centerKey) - centerOrder.indexOf(b.centerKey));
  }, [bottomNavs, currentCenter, hasCurrentCenterAccess, isSystem, pathname, systemCenters]);
  const fixedNavs = subCenter ? bottomNavs.filter((item) => !item.externalLink) : bottomNavs;

  const handleExternalLinkClick = (item: any) => {
    const url = buildExternalCenterUrlFromMenu(item);
    if (url) navigate(url);
  };

  useEffect(() => {
    const { _selKey } = filterCurrNav(pathname, [...navs, ...bottomNavs]);
    setSelKey(_selKey);
  }, [pathname, navs, bottomNavs]);

  return (
    <div className={`left-nav-shell${menuCollapsed ? ' is-collapsed' : ''}`}>
      {centerNavs.length > 0 && (
        <div className="left-center-nav">
          <div className="left-center-nav-list">
            {centerNavs.map((item) => {
              const active = item.centerKey === currentCenter;
              const title = NAV_MAP[item.title]?.title || item.title;
              const content = (
                <>
                  <RenderIcon size="18" Icon={item.icon} style={{ marginRight: 0 }} />
                  <span>{title}</span>
                </>
              );
              return (
                <Tooltip key={item.centerKey} content={title} position="right">
                  <Link
                    to={active ? pathname : item.targetPath || buildExternalCenterUrlFromMenu(item)}
                    className={active ? 'is-active' : undefined}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setMenuCollapsed(false)}
                  >
                    {content}
                  </Link>
                </Tooltip>
              );
            })}
          </div>
          {showFunctionMenu && (
            <Tooltip content={t(menuCollapsed ? 'components:expand' : 'components:collapse')} position="right">
              <button
                type="button"
                className="left-center-nav-collapse"
                aria-label={t(menuCollapsed ? 'components:expand' : 'components:collapse')}
                onClick={() => setMenuCollapsed((collapsed) => !collapsed)}
              >
                {menuCollapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </button>
            </Tooltip>
          )}
        </div>
      )}
      {showFunctionMenu && (
        <div
          className="left-menu-wrapper"
          style={{
            width: menuCollapsed ? 0 : 208,
            overflow: 'hidden',
            transition: 'width 0.3s ease',
          }}
        >
          <Menu
            style={{ width: 208, height: '100%', paddingTop: 16 }}
            selectedKeys={[selKey]}
            hasCollapseButton={false}
            onClickMenuItem={(key) => {
              !key.includes('external:') && navigate(key);
            }}
          >
            {currentCenter && hasCurrentCenterAccess && (
              <Tooltip content={NAV_MAP[systemCenter?.title || SUB_CENTER_ROOT_TITLE[subCenter]]?.title} position="right">
                <div className="left-menu-current-center">{NAV_MAP[systemCenter?.title || SUB_CENTER_ROOT_TITLE[subCenter]]?.title}</div>
              </Tooltip>
            )}
            {navs?.length > 0 && (
              <div className={'left-menu-nav left-menu-nav-top'}>
                {navs.map((item) => {
                  if (!item?.children?.length) {
                    return renderLeafMenuItem(item, 0);
                  }

                  return (
                    <MenuItemGroup key={item.title} title={renderGroupTitle(item)}>
                      {item.children.map((child) => {
                        if (!child.children?.length) {
                          return renderLeafMenuItem(child);
                        }

                        return (
                          <MenuItemGroup key={child.title} title={renderGroupTitle(child)}>
                            {child.children.map((subChild) => renderLeafMenuItem(subChild))}
                          </MenuItemGroup>
                        );
                      })}
                    </MenuItemGroup>
                  );
                })}
              </div>
            )}
            {fixedNavs?.length > 0 && (
              <div className={'left-menu-nav left-menu-nav-bottom'}>
                {fixedNavs.map((item) => {
                  if (item.externalLink) {
                    return (
                      <MenuItem
                        key={`external:${item.centerKey}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleExternalLinkClick(item)}
                      >
                        <RenderIcon size="16" Icon={item.icon} />
                        {NAV_MAP[item.title]?.title || item.title}
                        <IconLaunch
                          style={{
                            marginLeft: 12,
                            fontSize: 16,
                            position: 'absolute',
                            right: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </MenuItem>
                    );
                  }
                  return renderLeafMenuItem(item, 0);
                })}
              </div>
            )}

            {!navs?.length && !fixedNavs?.length && (
              <div style={{ textAlign: 'center', marginTop: 16, color: 'var(--text-color-placeholder)' }}>无权限，请联系管理员配置</div>
            )}
          </Menu>
        </div>
      )}
    </div>
  );
}

export default Nav;
